// Client side of the Inbox live stream (GET /api/inbox/stream).
//
// Replaces the Inbox's five-second poll of an expensive list query with a
// server push. EventSource carries the session cookie automatically on
// same-origin requests, so there is no separate auth handshake.
//
// The contract with the caller is deliberately two-part: `onChange` says
// "re-read now", and `onDegraded` says "the live channel is not delivering, so
// keep yourself fresh some other way". Without the second half a broken proxy
// or a capacity refusal would leave the Inbox silently frozen — worse than the
// polling it replaces.

import { apiUrl } from './client';

export interface InboxStreamHandlers {
  /** The server observed a change to something the Inbox displays. */
  onChange: () => void;
  /** true when the stream is not delivering and the caller should self-refresh. */
  onDegraded: (degraded: boolean) => void;
}

/** How long to wait for the server's `ready` frame before concluding that
 *  something between here and the server is swallowing the stream. */
const READY_TIMEOUT_MS = 10_000;

/** Our own reconnect ladder, used ONLY when the browser has given up (see
 *  below). EventSource handles ordinary network drops by itself. */
const RECONNECT_BASE_MS = 15_000;
const RECONNECT_MAX_MS = 120_000;

export function subscribeInboxChanges(handlers: InboxStreamHandlers): () => void {
  if (typeof EventSource === 'undefined') {
    handlers.onDegraded(true);
    return () => {};
  }

  let source: EventSource | null = null;
  let reconnectTimer: number | null = null;
  let readyTimer: number | null = null;
  let attempt = 0;
  let stopped = false;

  const clearReadyTimer = () => {
    if (readyTimer !== null) window.clearTimeout(readyTimer);
    readyTimer = null;
  };

  const scheduleReconnect = () => {
    if (stopped || reconnectTimer !== null) return;
    const delay = Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_MAX_MS);
    attempt += 1;
    reconnectTimer = window.setTimeout(() => {
      reconnectTimer = null;
      connect();
    }, delay);
  };

  const connect = () => {
    if (stopped) return;
    const es = new EventSource(apiUrl('/inbox/stream'), { withCredentials: true });
    source = es;

    // Headers arriving is not proof that frames flow — a buffering proxy opens
    // the response and then holds everything back. Only the server's own first
    // frame clears the degraded flag.
    clearReadyTimer();
    readyTimer = window.setTimeout(() => handlers.onDegraded(true), READY_TIMEOUT_MS);

    es.addEventListener('ready', () => {
      clearReadyTimer();
      attempt = 0;
      handlers.onDegraded(false);
    });

    es.addEventListener('change', () => handlers.onChange());

    es.addEventListener('error', () => {
      clearReadyTimer();
      handlers.onDegraded(true);
      // CONNECTING means EventSource is already retrying on its own; opening a
      // second socket alongside it would just double the load.
      if (es.readyState !== EventSource.CLOSED) return;
      // CLOSED means the browser gave up permanently — that is how it treats a
      // non-2xx response, including the server's at-capacity 503. Nothing will
      // reconnect unless we do it ourselves, on a slow ladder.
      es.close();
      if (source === es) source = null;
      scheduleReconnect();
    });
  };

  connect();

  return () => {
    stopped = true;
    clearReadyTimer();
    if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
    source?.close();
    source = null;
  };
}
